/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/Header/Header";
import {
  AddBoxOutlinedIcon,
  CancelOutlinedIcon,
  CloseIcon,
  DeleteOutlineOutlinedIcon,
  EditOutlinedIcon,
  LocalOfferOutlinedIcon,
  SearchOutlinedIcon,
  ViewCompactAltOutlinedIcon,
} from "../../Icons";
import {
  deleteProductById,
  getAllProducts,
  sendExpiration,
} from "../../api/api";
import OffersDetails from "../../components/Dialogs/OffersDetails";
import DeleteOffers from "../../components/Dialogs/DeleteOffers";
import { getTrimmedData } from "../../util";
import withTimedDisplay from "../../utils/timedAlert";
import ConfirmationDialog from "../../components/Dialogs/ConfirmationDialog";

const Offers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);
  const [expireDialogOpen, setExpireDialogOpen] = useState(false);
  const [unExpireDialogOpen, setUnExpireDialogOpen] = useState(false);
  const [offerToExpire, setOfferToExpire] = useState(null);
  const [offerToUnExpire, setOfferToUnExpire] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });

  const [sortOrder, setSortOrder] = useState("DESC");
  const theme = useTheme();
  const TimedAlert = withTimedDisplay(Alert);
  const [showAlert, setShowAlert] = useState(false);

  const breadcrumbsLinks = [
    {
      id: 2,
      title: "Offers",
      href: "/offers",
      icon: <LocalOfferOutlinedIcon fontSize="small" />,
    },
  ];

  const handleViewClick = (offer) => {
    setSelectedOffer(offer);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOffer(null);
  };

  const handleDeleteClick = (offer) => {
    setOfferToDelete(offer);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setOfferToDelete(null);
  };



  const handleExpireClick = (offer) => {
    setOfferToExpire(offer);
    setExpireDialogOpen(true);
  };

  const handleUnExpireClick = (offer) =>{
    setOfferToUnExpire(offer);
    setUnExpireDialogOpen(true);
  }
  const handleConfirmDelete = async () => {
    if (offerToDelete) {
      try {
        const response = await deleteProductById(offerToDelete.id);
        fetchOffers();
        setSuccess(response.data.message);
        setShowAlert(true);
        setDeleteDialogOpen(false);
        setOfferToDelete(null);
      } catch (err) {
        handleError(err);
      }
    }
  };

  const handleConfirmExpire = async () => {
    if (offerToExpire) {
      try {
        const response = await sendExpiration({
          is_expired: true,
          id: offerToExpire.id,
        });
        fetchOffers();
        setSuccess(response.data.message);
        setShowAlert(true);
        setExpireDialogOpen(false);
        setOfferToExpire(null);
      } catch (err) {
        handleError(err);
      }
    }
  };

  const handleConfirmUnExpire = async() =>{
       if (offerToUnExpire) {
      try {
        const response = await sendExpiration({
          is_expired: false,
          id: offerToUnExpire.id,
        });
        fetchOffers();
        setSuccess(response.data.message);
        setShowAlert(true);
        setUnExpireDialogOpen(false);
        setOfferToUnExpire(null);
      } catch (err) {
        handleError(err);
      }
    }
  }

  const handleError = (err) => {
    if (err.response && err.response.data) {
      if (
        err.response.data.display_errors &&
        err.response.data.display_errors.length > 0
      ) {
        setError(err.response.data.display_errors[0]);
        setShowAlert(true);
      } else {
        setError("An unexpected error occurred.");
        setShowAlert(true);
      }
    } else {
      setError("An unexpected error occurred.");
      setShowAlert(true);
    }
  };

  const fetchOffers = async (pageNumber = null, pageSize = null) => {
    setLoading(true);
    try {
      const currentPage =
        pageNumber !== null ? pageNumber : paginationModel.page + 1;
      const currentPageSize =
        pageSize !== null ? pageSize : paginationModel.pageSize;

      const response = await getAllProducts(
        currentPage,
        currentPageSize,
        sortOrder,
        searchQuery,
        ""
      );


      const totalPages = Math.ceil(response.data.totalCount / currentPageSize);

      if (currentPage > totalPages && totalPages > 0) {
        const newPageNumber = totalPages - 1;
        setPaginationModel((prevModel) => ({
          ...prevModel,
          page: newPageNumber,
        }));
        return fetchOffers(newPageNumber + 1, currentPageSize);
      }

      setOffers(response.data.products);
      setTotalCount(response.data.totalCount);

      if (pageNumber !== null) {
        setPaginationModel((prevModel) => ({
          ...prevModel,
          page: pageNumber - 1,
        }));
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, [paginationModel, sortOrder, searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  return (
    <Box m="0rem 2.5rem">
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Header breadcrumbsLinks={breadcrumbsLinks} />
        <Link to={`/offers/add`}>
          <Button
            startIcon={<AddBoxOutlinedIcon />}
            sx={{
              mx: 1,
              py: 1,
              background: theme.palette.primary[500],
              color: "white",
            }}
            variant="contained"
          >
            Add Offer
          </Button>
        </Link>
      </Box>

      <div className="flex justify-end">
        <TextField
          label="Search Offer"
          name="search"
          value={searchQuery}
          onChange={handleSearchChange}
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{ color: theme.palette.background.deepSea }}
              >
                <SearchOutlinedIcon />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <OffersDetails
        open={dialogOpen}
        onClose={handleCloseDialog}
        offer={selectedOffer}
      />

      <DeleteOffers
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        setShowAlert={setShowAlert}
        showAlert={showAlert}
        title={"Offer"}
      />

        <ConfirmationDialog
        open={expireDialogOpen}
        onClose={() => setExpireDialogOpen(false)}
        onConfirm={handleConfirmExpire}
        title={`Expire Offer`}
        message={`Are you sure you want to expire this offer?`}
        confirmButtonText="Expire Offer"
        error={error}
        showAlert={showAlert}
        setShowAlert={setShowAlert}
      />
        <ConfirmationDialog
        open={unExpireDialogOpen}
        onClose={() => setUnExpireDialogOpen(false)}
        onConfirm={handleConfirmUnExpire}
        title={`Revert Expire Offer`}
        message={`Do you want to revert back this expired offer?`}
        confirmButtonText="Revert Expire Offer"
        error={error}
        showAlert={showAlert}
        setShowAlert={setShowAlert}
      />

      <Box sx={{ height: "auto", mt: 2 }}>
        {success && showAlert && (
          <TimedAlert
            severity="success"
            setShowAlert={setShowAlert}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setSuccess(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {success}
          </TimedAlert>
        )}

        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            {offers.length === 0 && !showAlert ? (
              <div className="mt-2">
                <Alert setShowAlert={setShowAlert} severity="error">
                  No Offers Found
                </Alert>
              </div>
            ) : (
              <TableContainer>
                <Table sx={{ border: "1px solid #E0E0E0", mt: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "280px" }}>
                        Title
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "280px" }}>
                        Description
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Category
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Original Price
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Discount Price
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Merchant
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Expired</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {offers.map((offer) => (
                      <TableRow key={offer.id}>
                        <TableCell>
                          {getTrimmedData(offer.title, 100)}
                        </TableCell>
                        <TableCell>
                          {getTrimmedData(offer.description, 100)}
                        </TableCell>
                        <TableCell>{offer.category}</TableCell>
                        <TableCell>{`${offer.originalPrice}`}</TableCell>
                        <TableCell>{`${offer.discountPrice}`}</TableCell>
                        <TableCell>{`${offer.merchant}`}</TableCell>
                        <TableCell>{`${offer.is_expired}`}</TableCell>
                        <TableCell>
                          <Box
                          // display="flex"
                          // gap="4px"
                          // justifyContent="space-around"
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", lg: "row" },
                                justifyContent: "flex-start",
                                alignItems: "center",
                                marginBottom: "1rem",
                                gap: "0.95rem",
                              }}
                            >
                              <Link to={`/offers/${offer.id}`}>
                                <Button
                                  sx={{
                                    px: 2,
                                    background: theme.palette.primary[500],
                                    color: "white",
                                  }}
                                  startIcon={<EditOutlinedIcon />}
                                >
                                  Edit
                                </Button>
                              </Link>
                              <Button
                                onClick={() => handleDeleteClick(offer)}
                                sx={{
                                  px: 2,
                                  background: theme.palette.primary[500],
                                  color: "white",
                                }}
                                startIcon={<DeleteOutlineOutlinedIcon fontSize="small"/>}
                              >
                                Delete
                              </Button>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", lg: "row" },
                                justifyContent: "flex-start",
                                alignItems: "center",
                                gap: "0.95rem",
                              }}
                            >
                              <Button
                                onClick={() => handleViewClick(offer)}
                                sx={{
                                  px: 2,
                                  background: theme.palette.primary[500],
                                  color: "white",
                                }}
                                startIcon={<ViewCompactAltOutlinedIcon />}
                              >
                                View
                              </Button>
                              {offer.is_expired ? (
                                <Button
                                  onClick={() => handleUnExpireClick(offer)}
                                  sx={{
                                    px: 2,
                                    background: theme.palette.primary[500],
                                    color: "white",
                                  }}
                                  startIcon={<CancelOutlinedIcon />}
                                >
                                  UnExpire
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => handleExpireClick(offer)}
                                  sx={{
                                    px: 2,
                                    background: theme.palette.primary[500],
                                    color: "white",
                                  }}
                                  startIcon={<CancelOutlinedIcon />}
                                >
                                  Expire
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[2, 5, 10]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={paginationModel.pageSize}
                  page={paginationModel.page}
                  onPageChange={(event, newPage) => {
                    setPaginationModel({ ...paginationModel, page: newPage });
                  }}
                  onRowsPerPageChange={(event) => {
                    setPaginationModel({
                      page: 0,
                      pageSize: parseInt(event.target.value, 10),
                    });
                  }}
                />
              </TableContainer>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Offers;
